import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { baseQueryWithReauth } from './baseQuery';
import { parseQueryParams } from '../../utils';

const salesOrderApi = createApi({
  reducerPath: 'salesOrder',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'SalesOrders',
    'SalesOrderTypes',
    'SalesOrderApprovalMapping',
    'SalesOrderFormFieldMapping',
    'UniversalFieldSet',
    'SalesOrderAttachments',
    'SalesOrderLineItemAttachments',
    'SalesOrderDraft',
    'FormFieldMapping',
  ],
  endpoints: builder => ({
    getSalesOrderTypesDropdown: builder.query<
      BackendResponse<
        {
          id: number;
          name: string;
          description?: string;
        }[]
      >,
      void
    >({
      query: () => ({
        url: `/sales-order/sales-order-types-dropdown`,
        method: 'GET',
      }),
      providesTags: (__, error) => {
        if (error) {
          return [];
        }
        return ['SalesOrderTypes'];
      },
    }),
    getSalesOrders: builder.query<BackendResponse<any>, any>({
      query: params => ({
        url: `/sales-order${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: () => ['SalesOrders'],
    }),
    getSalesOrderById: builder.query<BackendResponse<any>, any>({
      query: id => ({
        url: `/sales-order/${id}`,
        method: 'GET',
      }),
      providesTags: (_, __, id) => [{ type: 'SalesOrders', id }],
    }),

    // Sales Order Material master details - DISABLED
    // Edit button uses lineItems data from GET /sales-order/:id
    // getSalesOrderMaterialById: builder.query<BackendResponse<any>, number | string>({
    //   query: id => ({
    //     url: `/material/${id}`,
    //     method: 'GET',
    //   }),
    // }),
    createSalesOrderDraft: builder.mutation<
      BackendResponse<any>,
      Partial<{ salesOrderTypeId: number }>
    >({
      query: params => ({
        url: `/sales-order/create-draft`,
        method: 'POST',
        body: params
          ? {
              salesOrderTypeId: params.salesOrderTypeId,
            }
          : {},
      }),
      invalidatesTags: ['SalesOrderDraft'],
    }),

    // Sales Order Approval Mapping endpoints
    getSalesOrderApprovalMapping: builder.query<
      BackendResponse<any>,
      Partial<{
        page: number;
        pageSize: number;
        companyGroupId: number;
        companyId: number;
        userId: number;
      }>
    >({
      query: params => ({
        url: `/sales-order-mappings/approval-config${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['SalesOrderApprovalMapping'],
    }),

    getSalesOrderApprovalHierarchy: builder.query<
      Blob,
      Partial<{
        requestType: string;
        userId: number;
      }>
    >({
      query: params => ({
        url: `/sales-order-mappings/approval-hierarchy${parseQueryParams(params)}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
      providesTags: ['SalesOrderApprovalMapping'],
    }),

    createSalesOrderApprovalMapping: builder.mutation<
      BackendResponse<any>,
      any
    >({
      query: payload => {
        return {
          url: `/sales-order-mappings/approval-config`,
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['SalesOrderApprovalMapping'];
      },
    }),

    updateSalesOrderApprovalMapping: builder.mutation<
      BackendResponse<any>,
      any
    >({
      query: payload => {
        const { id, ...rest } = payload;
        return {
          url: `/sales-order-mappings/approval-config/${id}`,
          method: 'PUT',
          body: rest,
        };
      },
      invalidatesTags: (_, error, { id }) => {
        if (error) {
          return [];
        }
        return [
          'SalesOrderApprovalMapping',
          { type: 'SalesOrderApprovalMapping', id: +id },
        ];
      },
    }),

    getSalesOrderApprovalMappingById: builder.query<
      BackendResponse<any>,
      number
    >({
      query: id => ({
        url: `/sales-order-mappings/approval-config/${id}`,
        method: 'GET',
      }),
      providesTags: (__, err, id) =>
        err ? [] : [{ type: 'SalesOrderApprovalMapping', id }],
    }),

    deleteSalesOrderApprovalMapping: builder.mutation<
      BackendResponse<any>,
      any
    >({
      query: payload => {
        return {
          url: `/sales-order-mappings/approval-config${parseQueryParams(payload)}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['SalesOrderApprovalMapping'];
      },
    }),

    // Sales Order Form Field Mapping endpoints
    getSalesOrderFormFieldMappings: builder.query<
      any,
      Partial<{
        page?: number;
        pageSize?: number;
        all?: boolean;
        search?: string;
      }>
    >({
      query: ({ page, pageSize, all, search }) => ({
        url: '/sales-order-field-mappings/form-field-mappings',
        params: { page, pageSize, all, search },
      }),
      providesTags: ['SalesOrderFormFieldMapping'],
    }),

    createSalesOrderFormFieldMapping: builder.mutation<any, any>({
      query: payload => ({
        url: '/sales-order-field-mappings/form-field-mappings',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['SalesOrderFormFieldMapping'],
    }),

    updateSalesOrderFormFieldMapping: builder.mutation<
      any,
      { id: number; body: any }
    >({
      query: ({ id, body }) => {
        return {
          url: `/sales-order-field-mappings/form-field-mappings/${id}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: ['SalesOrderFormFieldMapping'],
    }),

    deleteSalesOrderFormFieldMapping: builder.mutation<any, { ids: string[] }>({
      query: configId => ({
        url: '/sales-order-field-mappings/form-field-mappings',
        method: 'DELETE',
        body: { configId },
      }),
      invalidatesTags: ['SalesOrderFormFieldMapping'],
    }),

    getSalesOrderFormFieldMappingById: builder.query<any, number>({
      query: id => ({
        url: `/sales-order-field-mappings/form-field-mappings/${id}`,
        method: 'GET',
      }),
    }),

    getSalesOrderExactFormFieldMapping: builder.query<
      BackendResponse<any>,
      Partial<{
        salesOrderTypeId: string;
        companyId: string;
        companyGroupId: string;
      }>
    >({
      query: params => ({
        url: `/sales-order-field-mappings/form-field-mapping-exact${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['FormFieldMapping'],
    }),

    getSalesOrderUniversalField: builder.query<
      BackendResponse<{ count: number; list: any[] }>,
      any
    >({
      query: query => {
        return {
          url: `/sales-order-field-mappings/universal-set${parseQueryParams(query)}`,
          method: 'GET',
        };
      },
      providesTags: ['UniversalFieldSet'],
    }),

    createSalesOrderUniversalField: builder.mutation<BackendResponse<any>, any>(
      {
        query: payload => ({
          url: `/sales-order-field-mappings/universal-set`,
          method: 'POST',
          body: payload,
        }),
        invalidatesTags: (_, error) => (error ? [] : ['UniversalFieldSet']),
      },
    ),

    updateSalesOrderUniversalField: builder.mutation<
      BackendResponse<void>,
      { id: number; [key: string]: any }
    >({
      query: ({ id, ...rest }) => ({
        url: `/sales-order-field-mappings/universal-set/${id}`,
        method: 'PUT',
        body: rest,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['UniversalFieldSet']),
    }),

    deleteSalesOrderUniversalField: builder.mutation<
      BackendResponse<void>,
      number[] | string[] | number | string
    >({
      query: ids => ({
        url: `/sales-order-field-mappings/universal-set`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['UniversalFieldSet']),
    }),

    // Get form field mappings by sales order type
    getFormFieldMappingsBySalesOrderType: builder.query<
      any,
      {
        salesOrderTypeId: number;
        companyId?: number;
        companyGroupId?: number;
      }
    >({
      query: ({ salesOrderTypeId, companyId, companyGroupId }) => {
        const params = new URLSearchParams();
        if (companyId) params.append('companyId', companyId.toString());
        if (companyGroupId)
          params.append('companyGroupId', companyGroupId.toString());
        const queryString = params.toString();
        return {
          url: `/sales-order-field-mappings/form-field-mappings-by-sales-order-type/${salesOrderTypeId}${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['FormFieldMapping'],
    }),

    // Update sales order draft (using edit-draft endpoint as per documentation)
    updateSalesOrderDraft: builder.mutation<
      BackendResponse<any>,
      { id: number; formData: FormData | any }
    >({
      query: ({ id, formData }) => {
        // If formData is FormData, use it directly, otherwise send as JSON body (like Contract)
        const body = formData instanceof FormData ? formData : formData;

        return {
          url: `/sales-order/edit-draft/${id}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: (_, error, { id }) => {
        if (error) {
          return [];
        }
        return ['SalesOrderDraft', { type: 'SalesOrders', id }];
      },
    }),

    // Send sales order for approval (using PATCH /sales-order/:id as per documentation)
    sendSalesOrderForApproval: builder.mutation<BackendResponse<any>, number>({
      query: id => ({
        url: `/sales-order/${id}`,
        method: 'PATCH',
      }),
      invalidatesTags: (_, error, id) => {
        if (error) {
          return [];
        }
        return ['SalesOrders', { type: 'SalesOrders', id }];
      },
    }),

    // Send sales order for reapproval
    sendSalesOrderForReapproval: builder.mutation<BackendResponse<any>, number>(
      {
        query: id => ({
          url: `/sales-order/${id}/send-for-reapproval`,
          method: 'PATCH',
        }),
        invalidatesTags: (_, error, id) => {
          if (error) {
            return [];
          }
          return [
            'SalesOrders',
            { type: 'SalesOrders', id },
            { type: 'SalesOrderDraft', id },
          ];
        },
      },
    ),

    // Get sales order attachments
    getSalesOrderAttachments: builder.query<BackendResponse<any>, number>({
      query: salesOrderId => ({
        url: `/sales-order/${salesOrderId}/attachments`,
        method: 'GET',
      }),
      providesTags: (_, __, id) => [
        'SalesOrderAttachments',
        { type: 'SalesOrderAttachments', id },
      ],
    }),

    // Upload sales order attachment
    uploadSalesOrderAttachment: builder.mutation<
      BackendResponse<any>,
      {
        salesOrderId: number;
        file: File;
        documentType: string;
        remarks?: string;
      }
    >({
      query: ({ salesOrderId, file, documentType, remarks }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('documentType', documentType);
        if (remarks) formData.append('remarks', remarks);
        return {
          url: `/sales-order/${salesOrderId}/attachments/upload`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_, error, { salesOrderId }) => {
        if (error) {
          return [];
        }
        return [
          'SalesOrderAttachments',
          { type: 'SalesOrderAttachments', id: salesOrderId },
        ];
      },
    }),

    // Upload multiple sales order attachments (bulk)
    // Backend supports partial success: `data.uploaded` + `data.failed`
    uploadSalesOrderAttachments: builder.mutation<
      BackendResponse<any>,
      {
        salesOrderId: number;
        files: File[];
        attachments?: Array<{
          documentType?: string;
          remarks?: string | null;
        }>;
      }
    >({
      query: ({ salesOrderId, files, attachments }) => {
        const formData = new FormData();

        // Multiple files under the same key
        files.forEach(file => {
          formData.append('file', file);
        });

        // IMPORTANT: order of `files` and `attachments` must match
        formData.append('attachments', JSON.stringify(attachments ?? []));

        return {
          url: `/sales-order/${salesOrderId}/attachments/upload`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_, error, { salesOrderId }) => {
        if (error) {
          return [];
        }
        return [
          'SalesOrderAttachments',
          { type: 'SalesOrderAttachments', id: salesOrderId },
        ];
      },
    }),

    // Get sales order attachment file (RTK Query automatically creates lazy hook)
    getSalesOrderAttachmentFile: builder.query<
      Blob,
      { salesOrderId: number; attachmentId: number }
    >({
      query: ({ salesOrderId, attachmentId }) => ({
        url: `/sales-order/${salesOrderId}/attachments/file/${attachmentId}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    // Get sales order LINE ITEM attachment view (preview)
    // GET /sales-order/:salesOrderId/line-item-attachments/:attachmentId/view
    getSalesOrderLineItemAttachmentView: builder.query<
      Blob,
      { salesOrderId: number; attachmentId: number }
    >({
      query: ({ salesOrderId, attachmentId }) => ({
        url: `/sales-order/${salesOrderId}/line-item-attachments/${attachmentId}/view`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    // Get sales order LINE ITEM attachment download
    // GET /sales-order/:salesOrderId/line-item-attachments/:attachmentId/download
    getSalesOrderLineItemAttachmentFile: builder.query<
      Blob,
      { salesOrderId: number; attachmentId: number }
    >({
      query: ({ salesOrderId, attachmentId }) => ({
        url: `/sales-order/${salesOrderId}/line-item-attachments/${attachmentId}/download`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    // Delete sales order attachment
    deleteSalesOrderAttachment: builder.mutation<
      BackendResponse<any>,
      { salesOrderId: number; attachmentId: number }
    >({
      query: ({ salesOrderId, attachmentId }) => ({
        url: `/sales-order/${salesOrderId}/attachments/${attachmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error, { salesOrderId }) => {
        if (error) {
          return [];
        }
        return [
          'SalesOrderAttachments',
          { type: 'SalesOrderAttachments', id: salesOrderId },
        ];
      },
    }),

    // =========================
    // Line Item Attachments APIs
    // =========================
    // GET /sales-order/:salesOrderId/line-items/:lineItemId/attachments
    getSalesOrderLineItemAttachments: builder.query<
      BackendResponse<any>,
      { salesOrderId: number; lineItemId: number }
    >({
      query: ({ salesOrderId, lineItemId }) => ({
        url: `/sales-order/${salesOrderId}/line-items/${lineItemId}/attachments`,
        method: 'GET',
      }),
      providesTags: (_, error, { salesOrderId, lineItemId }) => {
        if (error) return [];
        return [
          'SalesOrderLineItemAttachments',
          {
            type: 'SalesOrderLineItemAttachments',
            id: `${salesOrderId}-${lineItemId}`,
          },
        ];
      },
    }),

    // POST /sales-order/:salesOrderId/line-items/:lineItemId/attachments (multipart)
    uploadSalesOrderLineItemAttachment: builder.mutation<
      BackendResponse<any>,
      {
        salesOrderId: number;
        lineItemId: number;
        file: File;
        documentType?: string;
        remarks?: string;
      }
    >({
      query: ({ salesOrderId, lineItemId, file, documentType, remarks }) => {
        const formData = new FormData();
        formData.append('file', file);
        if (documentType) formData.append('documentType', documentType);
        if (remarks) formData.append('remarks', remarks);
        return {
          url: `/sales-order/${salesOrderId}/line-items/${lineItemId}/attachments`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_, error, { salesOrderId, lineItemId }) => {
        if (error) return [];
        return [
          'SalesOrderLineItemAttachments',
          {
            type: 'SalesOrderLineItemAttachments',
            id: `${salesOrderId}-${lineItemId}`,
          },
        ];
      },
    }),

    // DELETE /sales-order/line-item-attachments/:attachmentId
    deleteSalesOrderLineItemAttachment: builder.mutation<
      BackendResponse<any>,
      { attachmentId: number; salesOrderId: number; lineItemId: number }
    >({
      query: ({ attachmentId }) => ({
        url: `/sales-order/line-item-attachments/${attachmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error, { salesOrderId, lineItemId }) => {
        if (error) return [];
        return [
          'SalesOrderLineItemAttachments',
          {
            type: 'SalesOrderLineItemAttachments',
            id: `${salesOrderId}-${lineItemId}`,
          },
        ];
      },
    }),

    // =========================
    // Actual Deliveries APIs
    // =========================
    // POST /sales-order/:salesOrderId/line-items/:lineItemId/actual-deliveries
    addSalesOrderLineItemActualDeliveries: builder.mutation<
      BackendResponse<any>,
      {
        salesOrderId: number;
        lineItemId: number;
        deliveries: {
          id?: number;
          qty: number;
          deliveryDate: string;
          remarks?: string;
          deliveryStatus?: string;
          _delete?: boolean;
          encapsulationLines?: any[];
          packingLines?: any[];
        }[];
      }
    >({
      query: ({ salesOrderId, lineItemId, deliveries }) => ({
        url: `/sales-order/${salesOrderId}/line-items/${lineItemId}/actual-deliveries`,
        method: 'POST',
        body: { deliveries },
      }),
      invalidatesTags: (_, error, { salesOrderId }) => {
        if (error) return [];
        return [
          'SalesOrders',
          { type: 'SalesOrders', id: salesOrderId },
          'SalesOrderDraft',
          { type: 'SalesOrderDraft', id: salesOrderId },
        ];
      },
    }),

    // Search materials (RTK Query automatically creates lazy hook)
    // Documentation: GET /master/material/all
    searchMaterials: builder.query<
      BackendResponse<any>,
      {
        search?: string;
        limit?: number;
        page?: number;
        pageSize?: number;
        sortBy?: string;
        sortOrder?: string;
        customerId?: number | string | null;
      }
    >({
      query: ({ search, limit, page, pageSize, sortBy, sortOrder, customerId }) => {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (limit) params.append('limit', limit.toString());
        if (page) params.append('page', page.toString());
        if (pageSize) params.append('pageSize', pageSize.toString());
        if (sortBy) params.append('sortBy', sortBy);
        if (sortOrder) params.append('sortOrder', sortOrder);
        const normalizedCustomerId = Number(customerId);
        if (Number.isFinite(normalizedCustomerId) && normalizedCustomerId > 0) {
          params.append('customerId', String(normalizedCustomerId));
        }
        return {
          url: `/master/material/all${params.toString() ? `?${params.toString()}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['SalesOrders'],
    }),

    exportMaterials: builder.query<
      Blob,
      {
        search?: string;
        sortBy?: string;
        sortOrder?: string;
        customerId?: number | string | null;
      }
    >({
      query: ({ search, sortBy, sortOrder, customerId }) => {
        const params = new URLSearchParams();
        if (search?.trim()) params.append('search', search.trim());
        if (sortBy) params.append('sortBy', sortBy);
        if (sortOrder) params.append('sortOrder', sortOrder);
        const normalizedCustomerId = Number(customerId);
        if (Number.isFinite(normalizedCustomerId) && normalizedCustomerId > 0) {
          params.append('customerId', String(normalizedCustomerId));
        }
        return {
          url: `/master/material/export${params.toString() ? `?${params.toString()}` : ''}`,
          method: 'GET',
          responseHandler: response => response.blob(),
        };
      },
    }),

    // Search customers (RTK Query automatically creates lazy hook)
    // Documentation: GET /master/customers/all
    searchCustomers: builder.query<
      BackendResponse<any>,
      {
        search?: string;
        limit?: number;
        page?: number;
        pageSize?: number;
        sortBy?: string;
        sortOrder?: string;
      }
    >({
      query: ({ search, limit, page, pageSize, sortBy, sortOrder }) => {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (limit) params.append('limit', limit.toString());
        if (page) params.append('page', page.toString());
        if (pageSize) params.append('pageSize', pageSize.toString());
        if (sortBy) params.append('sortBy', sortBy);
        if (sortOrder) params.append('sortOrder', sortOrder);
        return {
          url: `/master/customers/all${params.toString() ? `?${params.toString()}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['SalesOrders'],
    }),

    exportCustomers: builder.query<
      Blob,
      {
        search?: string;
        sortBy?: string;
        sortOrder?: string;
      }
    >({
      query: ({ search, sortBy, sortOrder }) => {
        const params = new URLSearchParams();
        if (search?.trim()) params.append('search', search.trim());
        if (sortBy) params.append('sortBy', sortBy);
        if (sortOrder) params.append('sortOrder', sortOrder);
        return {
          url: `/master/customers/export${params.toString() ? `?${params.toString()}` : ''}`,
          method: 'GET',
          responseHandler: response => response.blob(),
        };
      },
    }),

    // Get customer details by ID
    getSalesOrderCustomerDetails: builder.query<BackendResponse<any>, number>({
      query: id => ({
        // NOTE: backend /sales-order/customer/:id is returning 502 in dev;
        // Sales Order screens should use customer sales-defaults.
        url: `/customer/${id}/sales-defaults`,
        method: 'GET',
      }),
    }),

    // Same sales-defaults API with optional billToId/shipToId - returns Bill To & Ship To addresses for Billing & Shipping section
    getCustomerSalesDefaultsWithBillShip: builder.query<
      BackendResponse<any>,
      { customerId: number; billToId?: number; shipToId?: number }
    >({
      query: ({ customerId, billToId, shipToId }) => {
        const params = new URLSearchParams();
        if (billToId != null) params.append('billToId', String(billToId));
        if (shipToId != null) params.append('shipToId', String(shipToId));
        return {
          url: `/customer/${customerId}/sales-defaults${params.toString() ? `?${params.toString()}` : ''}`,
          method: 'GET',
        };
      },
    }),

    // Create Customer (used from Sales Order screens)
    createCustomer: builder.mutation<
      BackendResponse<any>,
      {
        name: string;
        code?: string;
        description?: string;
        companyId?: number | null;
        email?: string;
        panNo?: string;
        gstNo?: string;
        cityId?: number | null;
        incotermsId?: number | null;
        paymentTermsId?: number | null;
        salesOrganizationId?: number | null;
        distributionChannelId?: number | null;
        divisionId?: number | null;
        customerGroupId?: number | null;
        stateId?: number | null;
        countryId?: number | null;
        postalCode?: string;
        currencyId?: number | null;
        addressLine1?: string;
        addressLine2?: string;
        addressLine3?: string;
        region1Id?: number | null;
        region2Id?: number | null;
        region3Id?: number | null;
        customerContactInfos?: {
          departmentId?: number;
          name?: string;
          mobile?: string;
          email?: string;
          remarks?: string;
        }[];
      }
    >({
      query: body => ({
        url: `/customer`,
        method: 'POST',
        body,
      }),
    }),

    // Get Customer by ID — full customer record including customerContactInfos
    // GET /customer/:id
    getCustomerById: builder.query<BackendResponse<any>, number | string>({
      query: id => ({
        url: `/customer/${id}`,
        method: 'GET',
      }),
    }),

    // Edit Customer (partial update)
    // PATCH /customer/:id
    updateCustomer: builder.mutation<
      BackendResponse<any>,
      { id: number | string; body: Record<string, any> }
    >({
      query: ({ id, body }) => ({
        url: `/customer/${id}`,
        method: 'PATCH',
        body,
      }),
    }),

    // Edit Material (partial update)
    // PATCH /material/:id
    updateMaterial: builder.mutation<
      BackendResponse<any>,
      { id: number | string; body: Record<string, any> }
    >({
      query: ({ id, body }) => ({
        url: `/material/${id}`,
        method: 'PATCH',
        body,
      }),
    }),

    // Add line item to sales order
    // POST /sales-order/:id/line-items
    associateMaterialToSalesOrder: builder.mutation<
      BackendResponse<any>,
      { salesOrderId: number; materialId?: number; materialData?: any }
    >({
      query: ({ salesOrderId, materialId, materialData }) => ({
        url: `/sales-order/${salesOrderId}/line-items`,
        method: 'POST',
        body: {
          ...(materialId && { materialId }),
          ...materialData,
        },
      }),
      invalidatesTags: (_, error, { salesOrderId }) => {
        if (error) {
          return [];
        }
        return ['SalesOrders', { type: 'SalesOrders', id: salesOrderId }];
      },
    }),

    // Delete line item from sales order
    // DELETE /sales-order/:id/line-items/:lineItemId
    removeMaterialFromSalesOrder: builder.mutation<
      BackendResponse<any>,
      { salesOrderId: number; lineItemId: number }
    >({
      query: ({ salesOrderId, lineItemId }) => ({
        url: `/sales-order/${salesOrderId}/line-items/${lineItemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error, { salesOrderId }) => {
        if (error) {
          return [];
        }
        return ['SalesOrders', { type: 'SalesOrders', id: salesOrderId }];
      },
    }),

    // Add line item to sales order (alias for associateMaterialToSalesOrder)
    // POST /sales-order/:id/line-items
    createMaterialAndAssociateToSalesOrder: builder.mutation<
      BackendResponse<any>,
      { salesOrderId: number; materialData: any }
    >({
      query: ({ salesOrderId, materialData }) => ({
        url: `/sales-order/${salesOrderId}/line-items`,
        method: 'POST',
        body: materialData,
      }),
      invalidatesTags: (_, error, { salesOrderId }) => {
        if (error) {
          return [];
        }
        return ['SalesOrders', { type: 'SalesOrders', id: salesOrderId }];
      },
    }),

    // Update line item costing
    // PATCH /sales-order/:id/line-items/:lineItemId/costing
    updateLineItemCosting: builder.mutation<
      BackendResponse<any>,
      { salesOrderId: number; lineItemId: number; body: any }
    >({
      query: ({ salesOrderId, lineItemId, body }) => ({
        url: `/sales-order/${salesOrderId}/line-items/${lineItemId}/costing`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_, error, { salesOrderId }) => {
        if (error) return [];
        return ['SalesOrderDraft', { type: 'SalesOrders', id: salesOrderId }];
      },
    }),

    // Update line item in sales order
    // PATCH /sales-order/:id/line-items/:lineItemId
    updateMaterialThroughSalesOrder: builder.mutation<
      BackendResponse<any>,
      {
        salesOrderId: number;
        lineItemId: number;
        materialData: {
          requestedSchedules?: {
            id?: number;
            qty: number;
            scheduledDate: string;
            remarks?: string;
            _delete?: boolean;
          }[];
          actualDeliveries?: {
            id?: number;
            qty: number;
            deliveryDate: string;
            remarks?: string;
            deliveryStatus?: string;
            _delete?: boolean;
            encapsulationLines?: any[];
            packingLines?: any[];
          }[];
          [key: string]: unknown;
        };
      }
    >({
      query: ({ salesOrderId, lineItemId, materialData }) => ({
        url: `/sales-order/${salesOrderId}/line-items/${lineItemId}`,
        method: 'PATCH',
        body: materialData,
      }),
      invalidatesTags: (_, error, { salesOrderId }) => {
        if (error) {
          return [];
        }
        return ['SalesOrders', { type: 'SalesOrders', id: salesOrderId }];
      },
    }),

    getSoOrderLinesDropdown: builder.query<
      BackendResponse<any>,
      { type: 'ENCAP' | 'PACK' }
    >({
      query: ({ type }) => ({
        url: `/master/so-order-lines/dropdown?type=${type}`,
        method: 'GET',
      }),
    }),

    upsertActualDeliveryEncapsulationLines: builder.mutation<
      BackendResponse<any>,
      {
        salesOrderId: number;
        lineItemId: number;
        deliveryId: number;
        lines: any[];
      }
    >({
      query: ({ salesOrderId, lineItemId, deliveryId, lines }) => ({
        url: `/sales-order/${salesOrderId}/line-items/${lineItemId}/actual-deliveries/${deliveryId}/encapsulation-lines`,
        method: 'POST',
        body: { lines },
      }),
      invalidatesTags: (_, error, { salesOrderId }) => {
        if (error) return [];
        return ['SalesOrderDraft', { type: 'SalesOrders', id: salesOrderId }];
      },
    }),

    upsertActualDeliveryPackingLines: builder.mutation<
      BackendResponse<any>,
      {
        salesOrderId: number;
        lineItemId: number;
        deliveryId: number;
        lines: any[];
      }
    >({
      query: ({ salesOrderId, lineItemId, deliveryId, lines }) => ({
        url: `/sales-order/${salesOrderId}/line-items/${lineItemId}/actual-deliveries/${deliveryId}/packing-lines`,
        method: 'POST',
        body: { lines },
      }),
      invalidatesTags: (_, error, { salesOrderId }) => {
        if (error) return [];
        return ['SalesOrderDraft', { type: 'SalesOrders', id: salesOrderId }];
      },
    }),

    // Get sales order draft by ID (alias for getSalesOrderById)
    getSalesOrderDraftById: builder.query<BackendResponse<any>, number>({
      query: id => ({
        url: `/sales-order/${id}`,
        method: 'GET',
      }),
      providesTags: (_, __, id) => [
        { type: 'SalesOrderDraft', id },
        { type: 'SalesOrders', id },
      ],
    }),

    // Get recent sales order drafts
    getRecentSalesOrderDrafts: builder.query<BackendResponse<any>, void>({
      query: () => ({
        url: `/sales-order/recent-drafts`,
        method: 'GET',
      }),
      providesTags: ['SalesOrderDraft'],
    }),

    // Sales Order Approvals APIs
    getSalesOrderApprovals: builder.query<BackendResponse<any>, any>({
      query: params => ({
        url: `/sales-order-approvals${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['SalesOrders'],
    }),

    updateSalesOrderApproval: builder.mutation<BackendResponse<any>, any>({
      query: ({ id, ...rest }) => ({
        url: `/sales-order-approvals/${id}`,
        method: 'POST',
        body: { ...rest },
      }),
      invalidatesTags: (_, error, { id }) => {
        if (error) {
          return [];
        }
        return ['SalesOrders', { type: 'SalesOrders', id }];
      },
    }),

    bulkApproveSalesOrders: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: '/sales-order-approvals/bulk-approval-action',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['SalesOrders'],
    }),

    getSalesOrderApprovalLogsById: builder.query<
      BackendResponse<any>,
      { id: string; pageSize: number; page: number; sortBy?: string }
    >({
      query: ({ id, pageSize, page, sortBy }) => ({
        url: `/sales-order-approvals/logs/${id}`,
        method: 'GET',
        params: { page, pageSize, sortBy },
      }),
      providesTags: (__, err, { id }) =>
        err ? [] : [{ type: 'SalesOrders', id }],
    }),

    approveSalesOrderFromEmail: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/sales-order-approvals/approve-from-mail`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['SalesOrders'],
    }),

    resetSalesOrderApprovalHierarchy: builder.mutation<
      BackendResponse<any>,
      { id: number | string }
    >({
      query: ({ id }) => ({
        url: `/sales-order-approvals/reset-approval-hierarchy/${id}`,
        method: 'GET',
      }),
      invalidatesTags: (_, error, { id }) => {
        if (error) {
          return [];
        }
        return ['SalesOrders', { type: 'SalesOrders', id }];
      },
    }),

    rearrangeSalesOrderApprovalHierarchy: builder.mutation<
      BackendResponse<any>,
      { id: number | string; mappings: { approverId: number }[] }
    >({
      query: ({ id, mappings }) => ({
        url: `/sales-order-approvals/rearrange-approval-hierarchy/${id}`,
        method: 'POST',
        body: mappings,
      }),
      invalidatesTags: (_, error, { id }) => {
        if (error) {
          return [];
        }
        return ['SalesOrders', { type: 'SalesOrders', id }];
      },
    }),

    // Sales Order Line Item Reports API
    getSalesOrderLineItemReports: builder.query<BackendResponse<any>, any>({
      query: params => ({
        url: `/sales-order-line-item${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['SalesOrders'],
    }),

    // Export Sales Order Line Items to Excel
    exportSalesOrderLineItems: builder.query<Blob, any>({
      query: params => ({
        url: `/sales-order-line-item/export${parseQueryParams(params)}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    // Sales Order Reports APIs
    getSalesOrderReports: builder.query<BackendResponse<any>, any>({
      query: params => ({
        url: `/sales-order-reports${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['SalesOrders'],
    }),

    createSalesOrderAiReportShareUrl: builder.mutation<
      BackendResponse<{
        url: string;
        token?: string;
        dashboardId?: string;
        role?: string;
      }>,
      { dashboardId: string }
    >({
      query: body => ({
        url: '/sales-order-reporting-tool/dashboard-share-url',
        method: 'POST',
        body,
      }),
    }),

    getSalesOrderAiReportDashboard: builder.query<
      BackendResponse<{ dashboardId: string }>,
      void
    >({
      query: () => ({
        url: '/sales-order-reporting-tool/dashboard',
        method: 'GET',
      }),
    }),

    getSalesOrderReportChangeLogs: builder.query<
      BackendResponse<any>,
      { id: string; pageSize: number; page: number; sortBy?: string }
    >({
      query: ({ id, pageSize, page, sortBy }) => ({
        url: `/sales-order-reports/changelogs/${id}`,
        method: 'GET',
        params: { page, pageSize, sortBy },
      }),
      providesTags: (__, err, { id }) =>
        err ? [] : [{ type: 'SalesOrders', id }],
    }),

    exportSalesOrderReports: builder.query<
      Blob,
      { action: string; params?: any }
    >({
      query: ({ action, params }) => ({
        url: `/sales-order-reports/export/${action}${parseQueryParams(params || {})}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    // Sales Order Analytics APIs (Dashboard)
    getSalesOrderSummary: builder.query<BackendResponse<any>, void>({
      query: () => ({
        url: '/sales-order-analytics/summary',
        method: 'GET',
      }),
    }),

    getSalesOrderApprovalFunnel: builder.query<BackendResponse<any>, void>({
      query: () => ({
        url: '/sales-order-analytics/approval-funnel',
        method: 'GET',
      }),
    }),

    getSalesOrderPendingApprovalRequests: builder.query<
      BackendResponse<{ count: number }>,
      void
    >({
      query: () => ({
        url: '/sales-order-analytics/pending-approval-requests',
        method: 'GET',
      }),
    }),

    getSalesOrderRevenueMetrics: builder.query<
      BackendResponse<any>,
      { period?: string }
    >({
      query: ({ period }) => ({
        url: `/sales-order-analytics/revenue-metrics${period ? `?period=${period}` : ''}`,
        method: 'GET',
      }),
    }),

    getSalesOrderTopCustomers: builder.query<
      BackendResponse<any>,
      { limit?: number }
    >({
      query: ({ limit }) => ({
        url: `/sales-order-analytics/top-customers${limit ? `?limit=${limit}` : ''}`,
        method: 'GET',
      }),
    }),

    getSalesOrderTopMaterials: builder.query<
      BackendResponse<any>,
      { limit?: number }
    >({
      query: ({ limit }) => ({
        url: `/sales-order-analytics/top-materials${limit ? `?limit=${limit}` : ''}`,
        method: 'GET',
      }),
    }),

    getSalesOrderSalesByOrganization: builder.query<BackendResponse<any>, void>(
      {
        query: () => ({
          url: '/sales-order-analytics/sales-by-organization',
          method: 'GET',
        }),
      },
    ),

    getSalesOrderSalesByDistributionChannel: builder.query<
      BackendResponse<any>,
      void
    >({
      query: () => ({
        url: '/sales-order-analytics/sales-by-distribution-channel',
        method: 'GET',
      }),
    }),

    getSalesOrderSalesByPlant: builder.query<BackendResponse<any>, void>({
      query: () => ({
        url: '/sales-order-analytics/sales-by-plant',
        method: 'GET',
      }),
    }),

    getSalesOrderTypeDistribution: builder.query<BackendResponse<any>, void>({
      query: () => ({
        url: '/sales-order-analytics/sales-order-type-distribution',
        method: 'GET',
      }),
    }),

    getSalesOrderCustomerGroupDistribution: builder.query<
      BackendResponse<any>,
      void
    >({
      query: () => ({
        url: '/sales-order-analytics/customer-group-distribution',
        method: 'GET',
      }),
    }),

    getSalesOrderSalesByCountry: builder.query<BackendResponse<any>, void>({
      query: () => ({
        url: '/sales-order-analytics/sales-by-country',
        method: 'GET',
      }),
    }),

    getSalesOrderMonthlyOrderTrends: builder.query<
      BackendResponse<any>,
      { months?: number }
    >({
      query: ({ months }) => ({
        url: `/sales-order-analytics/monthly-order-trends${months ? `?months=${months}` : ''}`,
        method: 'GET',
      }),
    }),

    getSalesOrderDeliveryDateAnalytics: builder.query<
      BackendResponse<any>,
      void
    >({
      query: () => ({
        url: '/sales-order-analytics/delivery-date-analytics',
        method: 'GET',
      }),
    }),

    getSalesOrderCurrencyDistribution: builder.query<
      BackendResponse<any>,
      void
    >({
      query: () => ({
        url: '/sales-order-analytics/currency-distribution',
        method: 'GET',
      }),
    }),

    // Sales Order Change Logs
    getSalesOrderChangeLogsById: builder.query<
      BackendResponse<any>,
      { id: string; pageSize: number; page: number; sortBy?: string }
    >({
      query: ({ id, pageSize, page, sortBy }) => ({
        url: `/sales-order/change-logs/${id}`,
        method: 'GET',
        params: { page, pageSize, sortBy },
      }),
      providesTags: (__, err, { id }) =>
        err ? [] : [{ type: 'SalesOrders', id }],
    }),

    // Sales Order Activity Logs
    getSalesOrderActivityLogsById: builder.query<BackendResponse<any>, string>({
      query: id => ({
        url: `/sales-order/activity-logs/${id}`,
        method: 'GET',
      }),
      providesTags: (__, err, id) => (err ? [] : [{ type: 'SalesOrders', id }]),
    }),

    // Delete Sales Order
    deleteSalesOrder: builder.mutation<BackendResponse<any>, number>({
      query: id => ({
        url: `/sales-order/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SalesOrders'],
    }),

    // Print Sales Order
    printSalesOrder: builder.query<Blob, number>({
      query: id => ({
        url: `/sales-order/print/${id}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    // Download Sales Order JSON
    downloadSalesOrderJSON: builder.mutation<
      BackendResponse<any>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/sales-order/download/json`,
        method: 'POST',
        body: { id },
      }),
    }),

    // Download Sales Order Attachment JSON
    downloadSalesOrderAttachmentJSON: builder.query<
      BackendResponse<any>,
      number
    >({
      query: id => ({
        url: `/sales-order/download-attachment-json/${id}`,
        method: 'GET',
      }),
    }),

    // Download All Attachments as ZIP
    downloadSalesOrderAttachmentsZip: builder.query<Blob, number>({
      query: id => ({
        url: `/sales-order/${id}/attachments/download-zip`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    // Create Sales Order with Reference
    createSalesOrderWithReference: builder.mutation<
      BackendResponse<any>,
      { sourceSalesOrderId: number }
    >({
      query: ({ sourceSalesOrderId }) => ({
        url: `/sales-order/create-with-reference/${sourceSalesOrderId}`,
        method: 'POST',
      }),
      invalidatesTags: ['SalesOrders'],
    }),

    // Calculate Order Values
    calculateSalesOrderValues: builder.mutation<BackendResponse<any>, number>({
      query: id => ({
        url: `/sales-order/${id}/calculate-values`,
        method: 'POST',
      }),
      invalidatesTags: (_, error, id) => {
        if (error) {
          return [];
        }
        return [{ type: 'SalesOrders', id }];
      },
    }),

    // Get Filter Options
    getSalesOrderFilterOptions: builder.query<
      BackendResponse<any>,
      { pageType: string }
    >({
      query: ({ pageType }) => ({
        url: `/sales-order/filters?pageType=${pageType}`,
        method: 'GET',
      }),
    }),

    // Get Sort Options
    getSalesOrderSortOptions: builder.query<
      BackendResponse<any>,
      { pageType: string }
    >({
      query: ({ pageType }) => ({
        url: `/sales-order/sort-options?pageType=${pageType}`,
        method: 'GET',
      }),
    }),

    // Push to ERP
    pushSalesOrderToErp: builder.mutation<BackendResponse<any>, { id: number }>(
      {
        query: ({ id }) => ({
          url: `/sales-order/push/erp`,
          method: 'POST',
          body: { id },
        }),
        invalidatesTags: ['SalesOrders'],
      },
    ),

    // Get Sales Order for ERP Push or Export JSON
    getSalesOrderForErpPushOrExport: builder.query<
      BackendResponse<any>,
      number
    >({
      query: id => ({
        url: `/sales-order/${id}/push-to-erp-or-export-json`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetSalesOrdersQuery,
  useGetSalesOrderByIdQuery,
  useLazyGetSalesOrderByIdQuery,
  // useGetSalesOrderMaterialByIdQuery, // DISABLED - Edit button uses lineItems data
  // useLazyGetSalesOrderMaterialByIdQuery, // DISABLED - Edit button uses lineItems data
  useCreateSalesOrderDraftMutation,
  useGetSalesOrderTypesDropdownQuery,
  useGetSalesOrderApprovalMappingQuery,
  useGetSalesOrderApprovalMappingByIdQuery,
  useCreateSalesOrderApprovalMappingMutation,
  useUpdateSalesOrderApprovalMappingMutation,
  useDeleteSalesOrderApprovalMappingMutation,
  useLazyGetSalesOrderApprovalHierarchyQuery,
  // Sales Order Form Field Mapping hooks
  useGetSalesOrderFormFieldMappingsQuery,
  useCreateSalesOrderFormFieldMappingMutation,
  useUpdateSalesOrderFormFieldMappingMutation,
  useDeleteSalesOrderFormFieldMappingMutation,
  useGetSalesOrderFormFieldMappingByIdQuery,
  useGetSalesOrderExactFormFieldMappingQuery,
  useGetSalesOrderUniversalFieldQuery,
  // Sales Order Universal Field Set hooks
  useCreateSalesOrderUniversalFieldMutation,
  useUpdateSalesOrderUniversalFieldMutation,
  useDeleteSalesOrderUniversalFieldMutation,
  // Sales Order Draft and Approval hooks
  useGetFormFieldMappingsBySalesOrderTypeQuery,
  useUpdateSalesOrderDraftMutation,
  useSendSalesOrderForApprovalMutation,
  useSendSalesOrderForReapprovalMutation,
  useGetSalesOrderDraftByIdQuery,
  // Sales Order Attachments hooks
  useGetSalesOrderAttachmentsQuery,
  useUploadSalesOrderAttachmentMutation,
  useUploadSalesOrderAttachmentsMutation,
  useGetSalesOrderAttachmentFileQuery,
  useLazyGetSalesOrderAttachmentFileQuery,
  useGetSalesOrderLineItemAttachmentViewQuery,
  useLazyGetSalesOrderLineItemAttachmentViewQuery,
  useGetSalesOrderLineItemAttachmentFileQuery,
  useLazyGetSalesOrderLineItemAttachmentFileQuery,
  useDeleteSalesOrderAttachmentMutation,
  // Sales Order Material/Line Items hooks
  useSearchMaterialsQuery,
  useLazySearchMaterialsQuery,
  useLazyExportMaterialsQuery,
  // Sales Order Customer Search hooks
  useSearchCustomersQuery,
  useLazySearchCustomersQuery,
  useLazyExportCustomersQuery,
  useGetSalesOrderCustomerDetailsQuery,
  useLazyGetSalesOrderCustomerDetailsQuery,
  useLazyGetCustomerSalesDefaultsWithBillShipQuery,
  useGetCustomerByIdQuery,
  useLazyGetCustomerByIdQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useUpdateMaterialMutation,
  useAssociateMaterialToSalesOrderMutation,
  useRemoveMaterialFromSalesOrderMutation,
  useCreateMaterialAndAssociateToSalesOrderMutation,
  useUpdateMaterialThroughSalesOrderMutation,
  useGetSoOrderLinesDropdownQuery,
  useUpsertActualDeliveryEncapsulationLinesMutation,
  useUpsertActualDeliveryPackingLinesMutation,
  // Recent drafts hook
  useGetRecentSalesOrderDraftsQuery,
  // Sales Order Approvals hooks
  useGetSalesOrderApprovalsQuery,
  useUpdateSalesOrderApprovalMutation,
  useBulkApproveSalesOrdersMutation,
  useGetSalesOrderApprovalLogsByIdQuery,
  useApproveSalesOrderFromEmailMutation,
  // Sales Order Reports hooks
  useGetSalesOrderReportsQuery,
  useCreateSalesOrderAiReportShareUrlMutation,
  useGetSalesOrderAiReportDashboardQuery,
  useGetSalesOrderLineItemReportsQuery,
  useLazyGetSalesOrderLineItemReportsQuery,
  useLazyExportSalesOrderLineItemsQuery,
  useGetSalesOrderReportChangeLogsQuery,
  useLazyExportSalesOrderReportsQuery,
  // Sales Order Analytics hooks
  useGetSalesOrderSummaryQuery,
  useGetSalesOrderApprovalFunnelQuery,
  useGetSalesOrderPendingApprovalRequestsQuery,
  useGetSalesOrderRevenueMetricsQuery,
  useGetSalesOrderTopCustomersQuery,
  useGetSalesOrderTopMaterialsQuery,
  useGetSalesOrderSalesByOrganizationQuery,
  useGetSalesOrderSalesByDistributionChannelQuery,
  useGetSalesOrderSalesByPlantQuery,
  useGetSalesOrderTypeDistributionQuery,
  useGetSalesOrderCustomerGroupDistributionQuery,
  useGetSalesOrderSalesByCountryQuery,
  useGetSalesOrderMonthlyOrderTrendsQuery,
  useGetSalesOrderDeliveryDateAnalyticsQuery,
  useGetSalesOrderCurrencyDistributionQuery,
  // Sales Order Logs hooks
  useGetSalesOrderChangeLogsByIdQuery,
  useGetSalesOrderActivityLogsByIdQuery,
  // Sales Order Actions hooks
  useDeleteSalesOrderMutation,
  useLazyPrintSalesOrderQuery,
  useDownloadSalesOrderJSONMutation,
  useLazyDownloadSalesOrderAttachmentJSONQuery,
  useLazyDownloadSalesOrderAttachmentsZipQuery,
  useCreateSalesOrderWithReferenceMutation,
  useCalculateSalesOrderValuesMutation,
  useGetSalesOrderFilterOptionsQuery,
  useGetSalesOrderSortOptionsQuery,
  usePushSalesOrderToErpMutation,
  useLazyGetSalesOrderForErpPushOrExportQuery,
  // Line item attachments
  useGetSalesOrderLineItemAttachmentsQuery,
  useUploadSalesOrderLineItemAttachmentMutation,
  useDeleteSalesOrderLineItemAttachmentMutation,
  // Actual deliveries
  useAddSalesOrderLineItemActualDeliveriesMutation,
  useUpdateLineItemCostingMutation,
  useResetSalesOrderApprovalHierarchyMutation,
  useRearrangeSalesOrderApprovalHierarchyMutation,
} = salesOrderApi;

export default salesOrderApi;
